import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '@env/environment';
import type { AuthSession, LoginRequest, LoginResponse, StaffUser } from '../models/auth.model';
import { TokenStorage } from '../auth/token.storage';
import { RestaurantContextService } from './restaurant-context.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly tokenStorage = inject(TokenStorage);
  private readonly restaurantContext = inject(RestaurantContextService);

  private readonly sessionSignal = signal<AuthSession | null>(this.restoreSession());

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.sessionSignal()?.token);
  readonly currentUser = computed(() => this.sessionSignal()?.user ?? null);

  get token(): string | null {
    return this.sessionSignal()?.token ?? this.tokenStorage.getToken();
  }

  login(credentials: LoginRequest): Observable<AuthSession> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/login`, credentials)
      .pipe(
        map((res) => ({ token: res.accessToken, user: this.normalizeUser(res.user) })),
        tap((session) => this.persistSession(session)),
        catchError((err) => throwError(() => err)),
      );
  }

  refreshProfile(): Observable<StaffUser> {
    return this.http.get<StaffUser>(`${environment.apiUrl}/auth/me`).pipe(
      tap((user) => {
        const session = this.sessionSignal();
        if (session) {
          this.persistSession({ ...session, user: this.normalizeUser(user) });
        }
      }),
    );
  }

  logout(): void {
    this.tokenStorage.clearAll();
    this.restaurantContext.setGroupContext(null);
    this.sessionSignal.set(null);
    void this.router.navigate(['/login']);
  }

  private normalizeUser(user: StaffUser): StaffUser {
    const normalized = { ...user, permissions: user.permissions ?? [] };
    this.restaurantContext.setGroupContext(normalized.groupContext);
    return normalized;
  }

  private persistSession(session: AuthSession): void {
    const normalized = { ...session, user: this.normalizeUser(session.user) };
    this.tokenStorage.setToken(normalized.token);
    this.tokenStorage.setUser(normalized.user);
    this.sessionSignal.set(normalized);
  }

  private restoreSession(): AuthSession | null {
    const token = this.tokenStorage.getToken();
    const user = this.tokenStorage.getUser<StaffUser>();
    if (!token || !user) return null;
    return { token, user: this.normalizeUser(user) };
  }
}
